import React from 'react';
import { X, TrendingUp, DollarSign, Award, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value);
};

const ROIResults = ({ results, calculatorType, onClose, onReset }) => {
  const { t } = useTranslation();
  
  const isIndividual = calculatorType === 'individual';
  const accentColor = isIndividual ? 'emerald' : 'blue';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="roi-results-modal">
        {/* Header */}
        <div className={`bg-gradient-to-r ${isIndividual ? 'from-emerald-600 to-teal-600' : 'from-blue-600 to-indigo-600'} px-6 py-5 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('roiCalculator.results.title')}</h2>
                <p className={`${isIndividual ? 'text-emerald-100' : 'text-blue-100'} text-sm`}>
                  {isIndividual 
                    ? t('roiCalculator.results.individualSubtitle')
                    : t('roiCalculator.results.corporateSubtitle')}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-testid="roi-results-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* ROI Highlight */}
          <div className={`text-center py-8 px-4 rounded-xl ${isIndividual ? 'bg-emerald-50' : 'bg-blue-50'}`}>
            <p className="text-sm text-gray-500 mb-2">{t('roiCalculator.results.expectedROI')}</p>
            <div className={`text-5xl font-bold ${isIndividual ? 'text-emerald-600' : 'text-blue-600'}`}>
              {results.roiPercentage.toFixed(0)}%
            </div>
            <p className={`text-sm ${isIndividual ? 'text-emerald-600' : 'text-blue-600'} mt-2`}>
              {formatCurrency(results.netValue)} {t('roiCalculator.results.netValue')}
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className={`w-5 h-5 ${isIndividual ? 'text-emerald-500' : 'text-blue-500'}`} />
                <span className="text-sm text-gray-600">{t('roiCalculator.results.totalCost')}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(results.totalCosts)}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className={`w-5 h-5 ${isIndividual ? 'text-emerald-500' : 'text-blue-500'}`} />
                <span className="text-sm text-gray-600">{t('roiCalculator.results.totalValue')}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(results.totalValue)}</p>
            </div>
          </div>

          {/* Value Breakdown */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`${isIndividual ? 'bg-emerald-50' : 'bg-blue-50'} px-4 py-3 border-b`}>
              <h4 className="font-semibold text-gray-900">{t('roiCalculator.results.valueBreakdown')}</h4>
            </div>
            <div className="p-4 space-y-3">
              {results.breakdown && results.breakdown.map((item, index) => (
                <div key={item.label || index} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-4 h-4 ${isIndividual ? 'text-emerald-500' : 'text-blue-500'}`} />
                    <span className="text-gray-700">{item.label}</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Insights */}
          {results.insights && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Award className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-amber-800 mb-1">{t('roiCalculator.results.keyInsight')}</h5>
                  <p className="text-sm text-amber-700">{results.insights}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              onClick={onReset}
              variant="outline"
              className="flex-1"
              data-testid="roi-recalculate-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('roiCalculator.results.recalculate')}
            </Button>
            <Button 
              onClick={onClose}
              className={`flex-1 ${isIndividual ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              data-testid="roi-contact-btn"
            >
              {t('roiCalculator.results.getStarted')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROIResults;
